import React, { useEffect, useRef } from 'react';
import { useTestimonials } from '../hooks/useTestimonials';

export const TestimonialCarousel: React.FC = () => {
  const { testimonials, loading } = useTestimonials();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || testimonials.length === 0) return;

    let scrollPosition = 0;
    const scrollSpeed = 1; // pixels per frame
    const scrollWidth = scrollContainer.scrollWidth / 2; // We duplicate content, so half is the loop point

    const scroll = () => {
      scrollPosition += scrollSpeed;

      if (scrollPosition >= scrollWidth) {
        scrollPosition = 0;
      }

      scrollContainer.scrollLeft = scrollPosition;
    };

    const intervalId = setInterval(scroll, 30); // ~33fps

    return () => clearInterval(intervalId);
  }, [testimonials]);

  if (loading) {
    return (
      <div className="py-12 bg-white">
        <div className="text-center text-gray-500">Loading testimonials...</div>
      </div>
    );
  }

  if (testimonials.length === 0) {
    return null; // Don't show carousel if no testimonials
  }

  // Duplicate testimonials array for seamless loop
  const duplicatedTestimonials = [...testimonials, ...testimonials];

  return (
    <div className="py-16 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-12">
          What Our Clients Say
        </h2>

        <div className="relative">
          {/* Gradient overlays for smooth edges */}
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

          {/* Scrolling container */}
          <div
            ref={scrollRef}
            className="flex gap-8 overflow-x-hidden scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {duplicatedTestimonials.map((testimonial, index) => (
              <div
                key={`${testimonial.id}-${index}`}
                className="flex-shrink-0 bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100"
                style={{ width: '250px', height: '200px' }}
              >
                <img
                  src={testimonial.imageUrl}
                  alt={testimonial.name || 'Client testimonial'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-gray-600 mt-8">
          Trusted by {testimonials.length} satisfied clients
        </p>
      </div>
    </div>
  );
};